import React, { DependencyList, ReactElement, useCallback, useState } from 'react'
import { Typography } from '@equinor/eds-core-react'

import { useModal } from 'react-modal-hook'

import InfoDialog, { InfoDialogType } from '@/components/infoDialog'

export interface InfoDialogOptions<TData> {
    /** Dialog title. */
    title?: string

    /** Content of the dialog. */
    content?: string | ReactElement | ((data?: TData) => string | ReactElement)

    /** Called when dialog is confirmed. */
    onConfirm?: (data?: TData) => void

    /** Type of the info dialog. */
    type?: InfoDialogType
}

/**
 * Opens a confirmation dialog.
 *
 * Custom data can be passed to the dialog as argument of the `showInfo` function.
 *
 * @param options Info dialog options.
 * @param inputs An array of values referenced inside the dialog component.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useInfoDialog = <TData extends unknown>(options: InfoDialogOptions<TData>, inputs?: DependencyList) => {
    const [data, setData] = useState<TData>()

    const [showModal, hideModal] = useModal(() => {
        const content: string | ReactElement | undefined =
            typeof options.content === 'function' ? options.content(data) : options.content
        return (
            <InfoDialog title={options.title} onConfirmClick={onConfirmClick} type={options.type}>
                <>{typeof content === 'string' ? <Typography variant="body_short">{content}</Typography> : content}</>
            </InfoDialog>
        )
    }, [data, inputs])

    const onConfirmClick = () => {
        options.onConfirm?.(data)
        hideModal()
    }

    const showInfo = useCallback(
        (data?: TData) => {
            setData(data)
            showModal()
        },
        [showModal]
    )

    const hideInfo = useCallback(() => {
        hideModal()
    }, [hideModal])

    return {
        showInfo,
        hideInfo,
    }
}
